import { createContext, useContext, useState, ReactNode } from 'react';
import { translations } from '../i18n';

type Language = 'en' | 'ka' | 'ru';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string, params?: Record<string, string | number>) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

function getNestedValue(obj: any, path: string): string {
  return path.split('.').reduce((acc, part) => acc?.[part], obj) || path;
}

const SUPPORTED: Language[] = ['en', 'ka', 'ru'];

function initialLanguage(): Language {
  const stored = localStorage.getItem('language');
  if (stored && SUPPORTED.includes(stored as Language)) return stored as Language;
  const nav = navigator.language?.slice(0, 2).toLowerCase();
  if (nav && SUPPORTED.includes(nav as Language)) return nav as Language;
  return 'ka';
}

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [language, setLanguageState] = useState<Language>(initialLanguage);

  const setLanguage = (lang: Language) => {
    localStorage.setItem('language', lang);
    setLanguageState(lang);
  };

  const t = (key: string, params?: Record<string, string | number>) => {
    const translation = getNestedValue(translations[language], key);
    if (typeof translation !== 'string') return key;
    // Generic {token} interpolation (always supplies {year}).
    const tokens: Record<string, string> = {
      year: new Date().getFullYear().toString(),
      ...Object.fromEntries(Object.entries(params ?? {}).map(([k, v]) => [k, String(v)]))
    };
    return translation.replace(/\{(\w+)\}/g, (match, name) => tokens[name] ?? match);
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}